import React from 'react';
import { InputNumber } from 'antd';
import { FJ730 } from '../../../services/j218Api';
import LookupField from '../../../components/LookupField';

interface Tab2Props {
  getField: (key: keyof FJ730) => unknown;
  setField: (key: keyof FJ730, value: unknown) => void;
  isEditing: boolean;
}

const numField = (
  getField: (key: keyof FJ730) => unknown,
  setField: (key: keyof FJ730, value: unknown) => void,
  key: keyof FJ730,
  isEditing: boolean,
  _step?: string,
) => (
  <InputNumber value={Number(getField(key) || 0)}
    disabled={!isEditing}
    onChange={v => setField(key, v || 0)} />
);

const strField = (
  getField: (key: keyof FJ730) => unknown,
  setField: (key: keyof FJ730, value: unknown) => void,
  key: keyof FJ730,
  isEditing: boolean,
) => (
  <input value={String(getField(key) || '')} disabled={!isEditing}
    onChange={e => setField(key, e.target.value)} />
);

// Yarn type options per BR-F27 (fj730_204/214/224): 1=SD, 2=FD, 3=Yarn-dyed, 4=異形斷面, 5=Bright, 6=Trilobal
const yarnTypeOptions = [
  { value: 0, label: '(none)' },
  { value: 1, label: 'SD' },
  { value: 2, label: 'FD' },
  { value: 3, label: 'Yarn-dyed' },
  { value: 4, label: '異形斷面' },
  { value: 5, label: 'Bright' },
  { value: 6, label: 'Trilobal' },
];

// Warp/weft direction options (fj730_201/211/221 combobox)
const warpWeftOptions = [
  { value: 0, label: '(none)' },
  { value: 1, label: '經紗' },
  { value: 2, label: '緯紗' },
];

// Raw/processed options (fj730_205/215/225 combobox)
const rawProcessedOptions = [
  { value: 0, label: '(none)' },
  { value: 1, label: '原紗' },
  { value: 2, label: '加工紗' },
];

const selectField = (
  getField: (key: keyof FJ730) => unknown,
  setField: (key: keyof FJ730, value: unknown) => void,
  key: keyof FJ730,
  isEditing: boolean,
  options: { value: number; label: string }[],
) => (
  <select value={Number(getField(key) || 0)} disabled={!isEditing}
    onChange={e => setField(key, parseInt(e.target.value) || 0)}>
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

const Tab2WovenAnalysis: React.FC<Tab2Props> = React.memo(({ getField, setField, isEditing }) => (
  <div className="tab2-woven">
    <h4>經紗 #1</h4>
    <div className="form-grid-compact">
      <label>經/緯</label>{selectField(getField, setField, 'fj730_201', isEditing, warpWeftOptions)}
      <label>成份1</label>{strField(getField, setField, 'fj730_202', isEditing)}
      <label>成份2</label>{strField(getField, setField, 'fj730_203', isEditing)}
      <label>種類</label>{selectField(getField, setField, 'fj730_204', isEditing, yarnTypeOptions)}
      <label>原/加</label>{selectField(getField, setField, 'fj730_205', isEditing, rawProcessedOptions)}
      <label>成本</label>{numField(getField, setField, 'fj730_206', isEditing, '0.001')}
      <label>單位</label>{strField(getField, setField, 'fj730_207', isEditing)}
      <label>供應商</label>
      <LookupField lookupType="supplier" value={String(getField('fj730_208') || '')}
        onChange={(code) => setField('fj730_208', code)} disabled={!isEditing} title="選擇供應商" />
    </div>

    <h4>經紗 #2</h4>
    <div className="form-grid-compact">
      <label>經/緯</label>{selectField(getField, setField, 'fj730_211', isEditing, warpWeftOptions)}
      <label>成份1</label>{strField(getField, setField, 'fj730_212', isEditing)}
      <label>成份2</label>{strField(getField, setField, 'fj730_213', isEditing)}
      <label>種類</label>{selectField(getField, setField, 'fj730_214', isEditing, yarnTypeOptions)}
      <label>原/加</label>{selectField(getField, setField, 'fj730_215', isEditing, rawProcessedOptions)}
      <label>成本</label>{numField(getField, setField, 'fj730_216', isEditing, '0.001')}
      <label>單位</label>{strField(getField, setField, 'fj730_217', isEditing)}
      <label>供應商</label>
      <LookupField lookupType="supplier" value={String(getField('fj730_218') || '')}
        onChange={(code) => setField('fj730_218', code)} disabled={!isEditing} title="選擇供應商" />
    </div>

    <h4>緯紗</h4>
    <div className="form-grid-compact">
      <label>經/緯</label>{selectField(getField, setField, 'fj730_221', isEditing, warpWeftOptions)}
      <label>成份1</label>{strField(getField, setField, 'fj730_222', isEditing)}
      <label>成份2</label>{strField(getField, setField, 'fj730_223', isEditing)}
      <label>種類</label>{selectField(getField, setField, 'fj730_224', isEditing, yarnTypeOptions)}
      <label>原/加</label>{selectField(getField, setField, 'fj730_225', isEditing, rawProcessedOptions)}
      <label>成本</label>{numField(getField, setField, 'fj730_226', isEditing, '0.001')}
      <label>單位</label>{strField(getField, setField, 'fj730_227', isEditing)}
      <label>供應商</label>
      <LookupField lookupType="supplier" value={String(getField('fj730_228') || '')}
        onChange={(code) => setField('fj730_228', code)} disabled={!isEditing} title="選擇供應商" />
    </div>

    <h4>組織/密度</h4>
    <div className="form-grid-compact">
      <label>組織名稱</label>{strField(getField, setField, 'fj730_23', isEditing)}
      <label>經密</label>{numField(getField, setField, 'fj730_241', isEditing, '0.1')}
      <label>緯密</label>{numField(getField, setField, 'fj730_242', isEditing, '0.1')}
      <label>經總根</label>{numField(getField, setField, 'fj730_251', isEditing)}
      <label>%</label>{numField(getField, setField, 'fj730_252', isEditing)}
      <label>%</label>{numField(getField, setField, 'fj730_253', isEditing)}
      <label>重量</label>{numField(getField, setField, 'fj730_261', isEditing, '0.1')}
      <label>g/yd</label>{numField(getField, setField, 'fj730_262', isEditing, '0.1')}
    </div>

    <h4>織布廠</h4>
    <div className="form-grid-compact">
      <label>工繳代號</label>
      <LookupField lookupType="expenseCode" value={String(getField('fj730_27') || '')}
        onChange={(code) => setField('fj730_27', code)} disabled={!isEditing} title="選擇工繳代號" />
      <label>工繳單價</label>{numField(getField, setField, 'fj730_281', isEditing, '0.001')}
      <label>工繳單位</label>{strField(getField, setField, 'fj730_282', isEditing)}
      <label>供應商</label>
      <LookupField lookupType="supplier" value={String(getField('fj730_29') || '')}
        onChange={(code) => setField('fj730_29', code)} disabled={!isEditing} title="選擇供應商" />
      <label>工廠布號</label>{strField(getField, setField, 'fj730_291', isEditing)}
    </div>

    <h4>成品</h4>
    <div className="form-grid-compact">
      <label>織布廠</label>
      <LookupField lookupType="supplier" value={String(getField('fj730_41') || '')}
        onChange={(code) => setField('fj730_41', code)} disabled={!isEditing} title="選擇織布廠" />
      <label>織工(NTD)</label>{numField(getField, setField, 'fj730_42', isEditing, '0.001')}
      <label>單位</label>{strField(getField, setField, 'fj730_43', isEditing)}
      <label>機台</label>{strField(getField, setField, 'fj730_44', isEditing)}
      <label>織縮(損耗)%</label>{numField(getField, setField, 'fj730_45', isEditing, '0.01')}
    </div>
  </div>
));

Tab2WovenAnalysis.displayName = 'Tab2WovenAnalysis';
export default Tab2WovenAnalysis;
